/**
 * Service-worker reset helpers. Two callers depend on these:
 *
 *   1. {@link maybeResetServiceWorker} in `main.tsx` — the `?clear-sw=1`
 *      URL escape hatch. Manual fallback when something exotic goes
 *      wrong (corrupt SW state, etc).
 *
 *   2. {@link useRuntimeConfig.fetchOnce} in `lib/runtimeConfig.ts` —
 *      automatic trigger fired the moment we notice `/api/config`
 *      returns a different `loadedAt` than what we saw last time
 *      (meaning the helper process has restarted). The point is to
 *      relieve the user of ever having to know about `?clear-sw=1`:
 *      they click "一键启动服务" / restart the helper from the CLI
 *      / `nodemon` redeploys the dist — the SPA detects the new
 *      helper instance, drops the stale SW + caches, and reloads
 *      so the next mount picks up the freshly-built bundle.
 *
 * Both steps (delete caches AND unregister SW) are required:
 *
 *   - If we only delete CacheStorage entries, the still-registered
 *     SW will repopulate its precache from the live `/sw.js` and the
 *     stale bundle keeps coming back.
 *   - If we only unregister the SW, browsers keep the CacheStorage
 *     buckets around and a fresh `register()` may rehydrate them.
 *
 * Errors are swallowed deliberately — a partial cleanup is still better
 * than an exception bubbling up into the boot path. The caller (always
 * paired with a `location.reload()`) gets the fresh state on the next
 * tick regardless.
 */

/**
 * Hard-reset every Workbox-managed cache bucket AND unregister every
 * service worker bound to this origin. Idempotent; safe to call when
 * neither CacheStorage nor SW is available (older browsers, file://,
 * private mode).
 */
export async function resetServiceWorkerAndCaches(): Promise<void> {
  if (typeof window === 'undefined') return;
  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[adh] sw-reset: caches.delete partial failure:', err);
  }
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[adh] sw-reset: unregister partial failure:', err);
  }
}

/**
 * True when a service worker is actively controlling THIS page. We use
 * `navigator.serviceWorker.controller` rather than `getRegistration()`
 * because the latter resolves truthy even for SWs still in the
 * `installing` / `waiting` state — we only want to trigger an
 * auto-reset when there's a SW actually serving cached responses,
 * otherwise dev-mode (Vite, where no SW is registered) and very
 * first-loads (SW not yet activated) would also trip the reset path
 * unnecessarily.
 */
export function hasActiveServiceWorker(): boolean {
  if (typeof navigator === 'undefined') return false;
  if (!('serviceWorker' in navigator)) return false;
  return navigator.serviceWorker.controller != null;
}
